import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import { AdminService } from '../../../services/admin.service'
import toast from 'react-hot-toast'
import CreateDemoSessionBySlotModal from '../../../components/Modals/CreateDemoSessionUsingSlot'
import moment from 'moment'

const DemoDhunguru = () => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null)
    const [selectedSlots, setSelectedSlots] = useState<number[]>([])//Slots selected by the client to for available time
    const [lastSavedSlots, setLastSavedSlots] = useState<number[]>([])
    const [selectedSlotRanges, setSelectedSlotRanges] = useState<
        { startTime: string; endTime: string }[]
    >([])
    const [dragStart, setDragStart] = useState<number | null>(null)
    const [dragEnd, setDragEnd] = useState<number | null>(null)
    const [isDeselecting, setIsDeselecting] = useState<boolean>(false)
    const [selectedInstructor, setSelectedInstructor] = useState<{ value: string, label: string }>({
        value: '',
        label: ''
    });
    const [selectedInstrument, setSelectedInstrument] = useState<{ value: string, label: string }>({
        value: '',
        label: ''
    });
    const [instruments, setInstruments] = useState<{ value: string, label: string }[]>([])
    const [instructors, setInstructors] = useState<{ value: string, label: string }[]>([])
    const [bookedSlots, setBookedSlots] = useState<{ [key: number]: boolean }>({})
    const [showDemoSessionModal, setShowDemoSessionModal] = useState<boolean>(false)
    const [selectedSlot, setSelectedSlot] = useState<{ startTime: Date, endTime: Date }>({ startTime: moment().toDate(), endTime: moment().toDate() }) // This will hold the selected slot time for creating a demo session
    const [instrument, setInstrument] = useState<string>('');
    const [demoInfo, setDemoInfo] = useState<any>(null);

    const slots = Array.from({ length: 48 }, (_, i) => {
        const hour = Math.floor(i / 2)
        const minute = i % 2 === 0 ? 0 : 30
        const ampm = hour < 12 ? 'AM' : 'PM'
        const displayHour = hour % 12 === 0 ? 12 : hour % 12
        const label = `${displayHour}:${minute === 0 ? '00' : '30'} ${ampm}`
        const hour24 = hour.toString().padStart(2, '0')
        const min24 = minute === 0 ? '00' : '30'
        const time24 = `${hour24}:${min24}`
        return { index: i, label, time24 }
    })

    const getMinSelectableSlotIdx = () => {
        if (!selectedDate) return 0
        const now = new Date()
        const selected = new Date(selectedDate)
        if (
            selected.getFullYear() !== now.getFullYear() ||
            selected.getMonth() !== now.getMonth() ||
            selected.getDate() !== now.getDate()
        ) {
            return 0
        }
        const currentMinutes = now.getHours() * 60 + now.getMinutes()
        for (let i = 0; i < slots.length; i++) {
            const [h, m] = slots[i].time24.split(':').map(Number)
            const slotMinutes = h * 60 + m
            if (slotMinutes > currentMinutes) {
                return i
            }
        }
        return slots.length
    }

    const minSelectableSlotIdx = getMinSelectableSlotIdx()

    useEffect(() => {
        if (selectedInstrument && selectedInstrument.value) {
            AdminService.getInstructorBasedOnInstrument({ instrument: selectedInstrument.value }).then((res) => {
                if (res.status === 200) {
                    const options = res.data.instructors.map((ins: any) => ({
                        value: ins.instructor._id,
                        label: ins.instructor.firstName + (ins.instructor.lastName || "") + " (" + ins.instructor.email + ")"
                    }))
                    setInstructors(options)
                }
            })
                .catch((err) => {
                    console.error('Error fetching instructors:', err)
                    toast.error('Failed to fetch instructors. Please try again later.')
                })
        }
    }, [selectedInstrument]);


    useEffect(() => {
        AdminService.getAlInstruments().then((res) => {
            if (res.status === 200) {
                const options = res.data.instruments.map((instrument: string) => ({
                    value: instrument,
                    label: instrument.charAt(0).toUpperCase() + instrument.slice(1),
                }))
                setInstruments(options)
            }
        })
            .catch((err) => {
                console.error('Error fetching instruments:', err)
                toast.error('Failed to fetch instruments. Please try again later.')
            })
    }, [])

    useEffect(() => {
        const sorted = Array.from(new Set(selectedSlots)).sort((a, b) => a - b)
        const ranges = sorted.map(idx => {
            const start = slots[idx]
            const end = slots[idx + 1] || { time24: '00:00' }
            return {
                startTime: start.time24,
                endTime: end.time24,
            }
        })
        setSelectedSlotRanges(ranges)
    }, [selectedSlots])

    useEffect(() => {
        if (!selectedInstructor.value || !selectedDate) {
            setBookedSlots({})
            return
        }
        const data = {
            date: selectedDate?.toISOString(),
        }
        AdminService.getInstructorSlots(selectedInstructor.value, data).then((res) => {
            if (res.status === 200) {
                const booked: { [key: number]: boolean } = {}
                const selected: number[] = []
                let anyBooked = false

                res.data.slots.forEach((slot: { startTime: string, endTime: string, isBooked: boolean }) => {
                    const idx = slots.findIndex(
                        s => s.time24 === slot.startTime
                    )
                    if (idx !== -1) {
                        selected.push(idx)
                        if (slot.isBooked) {
                            booked[idx] = true
                            anyBooked = true
                        }
                    }
                })

                //From 48 slots, if any slot is overlapping with the busy slots, mark it as booked
                for (let i = 0; i < slots.length; i++) {
                    const slotTime = slots[i].time24; // e.g. "09:30"

                    const isBooked = res.data.busySlots.some((busySlot: { startTime: string, endTime: string }) => {
                        // Extract only HH:mm in local time
                        const busyStartTime = moment(busySlot.startTime).format("HH:mm");
                        const busyEndTime = moment(busySlot.endTime).format("HH:mm");

                        // Use dummy date so only time comparison happens
                        const slotMoment = moment(slotTime, "HH:mm");
                        const busyStart = moment(busyStartTime, "HH:mm");
                        const busyEnd = moment(busyEndTime, "HH:mm");

                        return slotMoment.isBetween(busyStart, busyEnd, undefined, '[]');
                    });

                    if (isBooked) {
                        booked[i] = true;
                    }
                }

                setBookedSlots(booked)
                setSelectedSlots(selected)
                setLastSavedSlots(selected)
            } else {
                setBookedSlots({})
                setSelectedSlots([])
                setLastSavedSlots([])
            }
        }).catch((err) => {
            console.error('Error fetching booked slots:', err)
            toast.error('Failed to fetch booked slots. Please try again later.')
        })
    }, [selectedInstructor, selectedDate])

    const isSlotInPast = (idx: number) => {
        if (!selectedDate) return false
        const now = new Date()
        const selected = new Date(selectedDate)
        if (
            selected.getFullYear() < now.getFullYear() ||
            (selected.getFullYear() === now.getFullYear() && selected.getMonth() < now.getMonth()) ||
            (selected.getFullYear() === now.getFullYear() && selected.getMonth() === now.getMonth() && selected.getDate() < now.getDate())
        ) {
            return true
        }
        if (
            selected.getFullYear() === now.getFullYear() &&
            selected.getMonth() === now.getMonth() &&
            selected.getDate() === now.getDate()
        ) {
            return idx < minSelectableSlotIdx
        }
        return false
    }

    const handleSlotMouseDown = (idx: number) => {
        if (bookedSlots[idx] || isSlotInPast(idx)) return
        setDragStart(idx)
        setDragEnd(idx)
        setIsDeselecting(selectedSlots.includes(idx))
        setSelectedSlots(prev => {
            if (!prev.includes(idx)) {
                return [...prev, idx]
            } else {
                return prev.filter(i => i !== idx)
            }
        })
    }

    const handleSlotMouseEnter = (idx: number) => {
        // console.log('Mouse entered slot:', idx)


        if (bookedSlots[idx] || isSlotInPast(idx)) return;
        if (!selectedSlots.includes(idx)) return;
        // console.log("PS1");

        let range: number[] = [];
        const start = idx;
        const end = idx;
        range = Array.from({ length: end - start + 1 }, (_, i) => start + i);

        // console.log("PS2");

        // If only one slot is selected, set selectedSlot with startTime and endTime
        if (range.length === 1 && selectedDate) {
            const slot = slots[range[0]];
            const nextSlot = slots[range[0] + 1];

            const [startHour, startMinute] = slot.time24.split(':').map(Number);
            const [endHour, endMinute] = nextSlot
                ? nextSlot.time24.split(':').map(Number)
                : [0, 0];

            const startTime = new Date(selectedDate);
            startTime.setHours(startHour, startMinute, 0, 0);

            const endTime = new Date(selectedDate);
            endTime.setHours(endHour, endMinute, 0, 0);
            // return;
            setDemoInfo({
                date: selectedDate,
                slot: {
                    startTime: slot.time24,
                    endTime: nextSlot ? (nextSlot.time24 === '00:00' ? '24:00' : nextSlot.time24) : '24:00'
                }
            });
            setSelectedSlot({ startTime, endTime });
            setShowDemoSessionModal(true);
        }
    }

    const handleSlotMouseUp = () => {
        setDragStart(null)
        setDragEnd(null)
        setIsDeselecting(false)
    }

    const isSaveEnabled = JSON.stringify([...selectedSlots].sort()) !== JSON.stringify([...lastSavedSlots].sort())

    return (
        <>
            <div className="container my-4">
                <h2 className="mb-4">Demo Availability</h2>

                <div className="mb-3">
                    <label className="form-label">Select Instrument</label>
                    <Select
                        options={instruments}
                        placeholder="Choose an instrument"
                        value={selectedInstrument.value ? selectedInstrument : null}
                        onChange={(option: any) => {
                            setSelectedInstrument(option)
                            setSelectedSlots([])
                            setLastSavedSlots([])
                        }}
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">Select Instructor</label>
                    <Select
                        options={instructors}
                        placeholder="Choose an instructor"
                        value={selectedInstructor.value ? selectedInstructor : null}
                        onChange={(option: any) => {
                            setSelectedInstructor(option)
                            setSelectedSlots([])
                            setLastSavedSlots([])
                        }}
                    />
                </div>

                <div className="mb-3">
                    <label className="form-label">Select Date</label>
                    <DatePicker
                        className="form-control"
                        selected={selectedDate}
                        onChange={date => {
                            setSelectedDate(date)
                            setSelectedSlots([])
                            setLastSavedSlots([])
                        }}
                        placeholderText="Choose a date"
                    />
                </div>

                <div>
                    {selectedDate && (
                        <>
                            <div
                                className="d-flex flex-wrap mt-4"
                                style={{ gap: 8 }}
                                onMouseUp={handleSlotMouseUp}
                                onMouseLeave={handleSlotMouseUp}
                            >
                                {slots.map((slot, idx) => {
                                    const nextSlot = slots[idx + 1]
                                    const endLabel = nextSlot ? nextSlot.label : '12:00 AM'
                                    const isBooked = bookedSlots[slot.index]
                                    const inPast = isSlotInPast(slot.index)
                                    return (
                                        <div
                                            key={slot.index}
                                            className={`border rounded text-center p-2 slot-cell ${isBooked
                                                ? 'bg-success text-white'
                                                : selectedSlots.includes(slot.index)
                                                    ? 'bg-primary text-white'
                                                    : inPast
                                                        ? 'bg-light text-muted'
                                                        : 'bg-light'
                                                }`}
                                            style={{
                                                width: 200,
                                                cursor: (isBooked || inPast || !selectedSlots.includes(slot.index)) ? 'not-allowed' : 'pointer',
                                                userSelect: 'none',
                                                opacity: isBooked || inPast ? 0.7 : 1,
                                            }}
                                            // onMouseDown={() => handleSlotMouseDown(slot.index)}
                                            onClick={() => handleSlotMouseEnter(slot.index)}
                                        >
                                            {`${slot.label} - ${endLabel}`}
                                        </div>
                                    )
                                })}
                            </div>

                            {/* <div className="mt-4">
                            <button
                                className="btn btn-primary"
                                disabled={!isSaveEnabled}
                                onClick={() => setLastSavedSlots([...selectedSlots])}
                            >
                                Save Slots
                            </button>
                        </div> */}
                        </>
                    )}
                </div>
            </div>
            <CreateDemoSessionBySlotModal
                show={showDemoSessionModal}
                setShow={setShowDemoSessionModal}
                instructor={selectedInstructor}
                demoTime={selectedSlot}
                demoInfo={demoInfo} // Pass the demoInfo to the modal
            />
        </>
    )
}

export default DemoDhunguru
